import React, { useMemo } from 'react';
import styled from 'styled-components';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  type GoalChartPlan,
  type GoalMilestone,
} from '../../features/goals/goalChartProjection';
import { Card } from './Card';

export interface GoalTrendPoint {
  date: string;
  value: number;
}

interface GoalTrendChartProps {
  title: string;
  unit: string;
  data: GoalTrendPoint[];
  goalPlan: GoalChartPlan | null;
}

interface CombinedPoint {
  date: string;
  value?: number;
  [key: string]: string | number | undefined;
}

const Container = styled(Card)`
  min-height: 350px;
  padding: 1.5rem;
  height: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  margin-bottom: 1rem;
`;

const Title = styled.h3`
  margin: 0;
  color: var(--text-primary);
  font-size: 1.1rem;
  font-weight: 600;
`;

const UnitLabel = styled.div`
  margin-top: 0.25rem;
  color: var(--text-secondary);
  font-size: 0.75rem;
`;

const Empty = styled.div`
  display: flex;
  min-height: 260px;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
`;

interface GoalTooltipProps {
  active?: boolean;
  label?: string;
  payload?: Array<{
    value?: number;
    dataKey?: string;
  }>;
  goalPlan: GoalChartPlan | null;
  unit: string;
}

const GoalTooltip: React.FC<GoalTooltipProps> = ({
  active,
  label,
  payload,
  goalPlan,
  unit,
}) => {
  if (!active || !label) return null;
  const milestone =
    goalPlan?.milestones.find((item) => item.deadlineMonth === label) ?? null;
  if (milestone) {
    return (
      <div className="custom-tooltip">
        <strong style={{ color: milestone.color }}>
          {milestone.shortLabel}
        </strong>
        <div>
          {milestone.targetValue.toFixed(1)} {goalPlan?.unit}
        </div>
      </div>
    );
  }

  const actualEntry = payload?.find(
    (entry) => entry.dataKey === 'value' && typeof entry.value === 'number',
  );
  if (actualEntry && typeof actualEntry.value === 'number') {
    return (
      <div className="custom-tooltip">
        <strong>
          {actualEntry.value.toFixed(1)} {unit}
        </strong>
      </div>
    );
  }

  const planEntries =
    goalPlan?.originalTrajectories.flatMap((trajectory) => {
      const entry = payload?.find(
        (item) =>
          item.dataKey === `original_${trajectory.goalId}` &&
          typeof item.value === 'number',
      );
      if (!entry || typeof entry.value !== 'number') return [];
      return [
        {
          shortLabel: trajectory.shortLabel,
          color: trajectory.color,
          value: entry.value,
        },
      ];
    }) ?? [];
  if (planEntries.length > 0) {
    return (
      <div className="custom-tooltip">
        {planEntries.map((entry) => (
          <div key={entry.shortLabel} style={{ color: entry.color }}>
            <strong>
              {entry.shortLabel}: {entry.value.toFixed(1)} {unit}
            </strong>
          </div>
        ))}
      </div>
    );
  }

  return null;
};

export const GoalTrendChart: React.FC<GoalTrendChartProps> = ({
  title,
  unit,
  data,
  goalPlan,
}) => {
  const chartData = useMemo<CombinedPoint[]>(() => {
    const pointsByDate = new Map<string, CombinedPoint>();
    data.forEach((point) => pointsByDate.set(point.date, { ...point }));

    goalPlan?.originalTrajectories.forEach((trajectory) => {
      trajectory.points.forEach((point) => {
        pointsByDate.set(point.date, {
          ...(pointsByDate.get(point.date) ?? { date: point.date }),
          [`original_${trajectory.goalId}`]: point.value,
        });
      });
    });

    goalPlan?.requiredPace.forEach((point) => {
      pointsByDate.set(point.date, {
        ...(pointsByDate.get(point.date) ?? { date: point.date }),
        required_pace: point.value,
      });
    });

    goalPlan?.benchmarks.forEach((benchmark) => {
      benchmark.points.forEach((point) => {
        pointsByDate.set(point.date, {
          ...(pointsByDate.get(point.date) ?? { date: point.date }),
          [`benchmark_${benchmark.goalId}`]: point.value,
        });
      });
    });

    return Array.from(pointsByDate.values()).sort((a, b) =>
      a.date.localeCompare(b.date),
    );
  }, [data, goalPlan]);

  if (chartData.length === 0) {
    return (
      <Container>
        <Header>
          <Title>{title}</Title>
          <UnitLabel>{unit}</UnitLabel>
        </Header>
        <Empty>No hay datos disponibles para este período.</Empty>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>{title}</Title>
        <UnitLabel>{unit}</UnitLabel>
      </Header>
      <ResponsiveContainer width="100%" height={280}>
        <LineChart
          data={chartData}
          margin={{ top: 8, right: 24, bottom: 8, left: 8 }}
        >
          <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            stroke="var(--text-secondary)"
            tickLine={false}
            tickFormatter={(value: string) =>
              new Intl.DateTimeFormat('es-ES', {
                month: 'short',
                year: '2-digit',
                timeZone: 'UTC',
              }).format(new Date(`${value}-01T00:00:00Z`))
            }
          />
          <YAxis
            stroke="var(--text-secondary)"
            tickLine={false}
            unit={unit}
            domain={[0, 100]}
          />
          <Tooltip content={<GoalTooltip goalPlan={goalPlan} unit={unit} />} />
          <Line
            type="monotone"
            dataKey="value"
            name="Real"
            stroke="var(--brand-600)"
            strokeWidth={2}
            legendType="none"
          />
          {goalPlan?.originalTrajectories.map((trajectory) => (
            <Line
              key={`original_${trajectory.goalId}`}
              type="linear"
              dataKey={`original_${trajectory.goalId}`}
              name={trajectory.shortLabel}
              stroke={trajectory.color}
              strokeWidth={1.75}
              strokeDasharray="3 3"
              connectNulls
              dot={false}
              legendType="none"
            />
          ))}
          {goalPlan?.benchmarks.map((benchmark) => (
            <Line
              key={`benchmark_${benchmark.goalId}`}
              type="linear"
              dataKey={`benchmark_${benchmark.goalId}`}
              name={benchmark.shortLabel}
              stroke={benchmark.color}
              strokeWidth={1.5}
              strokeDasharray="1 4"
              connectNulls
              dot={false}
              legendType="none"
              opacity={0.7}
            />
          ))}
          {goalPlan?.requiredPace.length ? (
            <Line
              type="linear"
              dataKey="required_pace"
              name="Ritmo"
              stroke="var(--brand-700)"
              strokeWidth={2}
              strokeDasharray="7 5"
              connectNulls
              dot={false}
              legendType="none"
            />
          ) : null}
          {goalPlan?.milestones.map((milestone: GoalMilestone) => (
            <ReferenceDot
              key={milestone.goalId}
              x={milestone.deadlineMonth}
              y={milestone.targetValue}
              r={6}
              fill={
                milestone.sequenceStatus === 'legacy_invalid'
                  ? 'var(--surface)'
                  : milestone.color
              }
              stroke={milestone.color}
              strokeWidth={
                milestone.sequenceStatus === 'legacy_invalid' ? 3 : 2
              }
              label={{
                value: milestone.shortLabel,
                position: 'top',
                fill: milestone.color,
                fontSize: 10,
              }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Container>
  );
};
